import { useContext, useEffect, useMemo, useState } from "react";
import { Form, Select, message } from "antd";
import {
  createProjectIntegration,
  getAccountProjects
} from "services/integration-service";

import { CheckCircleOutlined } from "@ant-design/icons";
import { IntegrationType } from "pages/subscription-settings/integrations-tab-new/utils";
import { FormInstance, useWatch } from "antd/lib/form/Form";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { ProjectIntegrationsTabSharedContext } from "pages/project-setting-menu/project-integrations/project-integrations-tab-context";
import AutodeskWelcomeLogoIcon from "components/svg-icons/autodesk-welcome-logo-icon";

export default function WelcomeAutodeskInitialIntegration(props: {
  integrationForm: FormInstance<any>;
  onProjectIntegrated: (response: any) => {};
}) {
  const { onProjectIntegrated, integrationForm } = props;

  const { sharedState, dispatch } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const { tokenRetrievalState }: TProjectContext = useContext(ProjectContext);

  const [projects, setProjects] = useState<any>(null);

  const companies = useMemo(() => {
    const comp: any = [];
    const subConfigs = sharedState?.subscriptionIntegrationConfigs?.filter(
      (subConfig: any) => {
        return (
          subConfig.integration.system ===
          IntegrationType[IntegrationType.AUTODESK]
        );
      }
    );
    subConfigs?.forEach((config: any) => {
      comp.push({
        name: config.properties.AUTODESK_ACCOUNT_NAME,
        id: config.properties.AUTODESK_ACCOUNT_ID,
        subscriptionId: config.id
      });
    });
    return comp.filter((company: any) => company.id);
  }, [sharedState.subscriptionIntegrationConfigs]);

  const selectedCompanyId = useWatch("account_id", integrationForm);

  useEffect(() => {
    if (!selectedCompanyId || !tokenRetrievalState.token) return;

    const getAccounts = async () => {
      const foundCompany = companies.find((company: any) => {
        return company.id === selectedCompanyId;
      });

      const projectsResp = await getAccountProjects(
        sharedState?.system,
        {
          accountId: foundCompany.id,
          oauthOperationId: sharedState?.authData?.id
        },
        tokenRetrievalState?.token
      );

      if (projectsResp.success) {
        setProjects(projectsResp.data);
      }
    };
    getAccounts();
  }, [
    companies,
    selectedCompanyId,
    sharedState?.authData?.id,
    sharedState?.system,
    tokenRetrievalState.token
  ]);

  const onFinish = (values: any) => {
    const foundCompany = companies.find((company: any) => {
      return company.id === values.account_id;
    });

    const foundProject = projects.find((project: any) => {
      return project.id === values.project_id;
    });
    const payload: any = [
      {
        properties: {
          integration_name: sharedState?.additionalData?.newIntegrationName,
          autodesk_project_id: values.project_id.toString(),
          autodesk_project_name: foundProject.name,
          project_alert_email: "integrations-nonprod@constructivIQ.com"
        },
        subscription_config_id: foundCompany.subscriptionId,
        oauth_operation_id: sharedState?.authData?.id
      }
    ];

    console.log(payload);

    const saveData = async () => {
      const createResponse = await createProjectIntegration(
        IntegrationType[IntegrationType.AUTODESK],
        payload,
        tokenRetrievalState.token
      );

      if (createResponse.success) {
        // message.success("Integration created successfully.");
        onProjectIntegrated(createResponse);
        dispatch({
          type: "SET_ACTIVE_LIST_VIEW"
        });
      } else {
        message.error("Failed to create integration.");
      }
    };
    saveData();
  };

  const companyListOptions = useMemo(() => {
    return companies
      ?.map((company: any) => {
        return {
          label: company.name,
          value: company.id
        };
      })
      .sort((c1: any, c2: any) =>
        c1.label.toLowerCase() > c2.label.toLowerCase() ? 1 : -1
      );
  }, [companies]);

  const projectListOptions = useMemo(() => {
    return projects
      ?.map((project: any) => {
        return {
          label: project.name,
          value: project.id
        };
      })
      .sort((p1: any, p2: any) =>
        p1.label.toLowerCase() > p2.label.toLowerCase() ? 1 : -1
      );
  }, [projects]);

  return (
    <Form
      form={integrationForm}
      onFinish={onFinish}
      layout="vertical"
      className="h-full flex flex-col py-16 w-full md:max-w-screen-sm mx-auto justify-center items-center"
    >
      <div className="flex justify-center items-center space-x-1 p-2">
        <AutodeskWelcomeLogoIcon />
        <div>Autodesk Construction Cloud</div>
      </div>
      <div className="flex justify-center items-center space-x-1 p-1 bg-ciq-success-bg border-ciq-success-border border border-solid rounded">
        <CheckCircleOutlined className="text-ciq-success-text" />
        <div>Signed in as {sharedState.authData?.output?.user_info?.email}</div>
      </div>
      <div className="grow  py-4">
        <div className="mt-7 w-[500px] flex-col space-y-7">
          <Form.Item
            name="account_id"
            className="hide-required-mark"
            label={
              <div className="flex items-center">
                <span>ACCOUNT</span>
              </div>
            }
            rules={[{ required: true, message: "Please select an Account" }]}
            requiredMark={false}
          >
            <Select
              showSearch
              optionFilterProp="label"
              // mode="multiple"
              options={companyListOptions}
              // tagRender={tagRender}
              placeholder="Select an Account"
              className="normal-case"
              loading={!companyListOptions}
              allowClear={false}
              showArrow
            />
          </Form.Item>
          <Form.Item
            name="project_id"
            className="hide-required-mark"
            label={
              <div className="flex items-center">
                <span>PROJECT</span>
              </div>
            }
            rules={[{ required: true, message: "Please select a Project" }]}
            requiredMark={false}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={projectListOptions}
              placeholder="Select a Project"
              className="normal-case"
              loading={selectedCompanyId && !projectListOptions}
              allowClear={false}
              showArrow
              disabled={!selectedCompanyId || !projectListOptions}
            />
          </Form.Item>
        </div>
      </div>
    </Form>
  );
}
